import { AxiosError } from 'axios';
import { useMemo } from 'react';
import { toaster } from '@optiaxiom/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useOpalUser } from '../../../hooks/useOpalUser';
import { UserInstructionService } from '../services/UserInstructionService';
import type { UserInstruction } from '../types';

const LIST_STALE_TIME = 1000 * 60;

export type ErrorInformation = {
  isError: boolean;
  error?: unknown;
};

export type UserInstructionHook = ErrorInformation & {
  isLoading: boolean;
  isCreating: boolean;
  isUpdating: boolean;
  isRemoving: boolean;
  userInstructions?: UserInstruction[];
  removeInstruction: (instructionGuid: string) => Promise<void>;
  updateInstruction: (instruction: UserInstruction) => Promise<void>;
  createNewInstruction: (userInstruction: UserInstruction) => Promise<void>;
};

export function useUserInstruction({
  instructionType,
}: {
  instructionType: 'system' | 'user';
}): UserInstructionHook {
  const queryClient = useQueryClient();
  const { opalUser } = useOpalUser();

  const userInstructionService: UserInstructionService = useMemo(() => {
    return new UserInstructionService(
      opalUser?.instance.customer_id ?? '',
      opalUser?.instance.instance_id ?? '',
      instructionType === 'system'
    );
  }, [opalUser, instructionType]);

  const {
    data: userInstructions,
    error,
    isLoading,
    isError,
  } = useQuery({
    queryKey: userInstructionService?.getUserInstructions().key,
    queryFn: userInstructionService?.getUserInstructions(),
    staleTime: LIST_STALE_TIME,
  });

  const deleteInstructionMutation = useMutation(
    {
      mutationFn: ({
        instructionGuid,
        isSystemInstruction,
      }: {
        instructionGuid: string;
        isSystemInstruction: boolean;
      }) => userInstructionService?.deleteUserInstruction(instructionGuid, isSystemInstruction),
      onSuccess: () => {
        void queryClient.invalidateQueries({
          queryKey: userInstructionService?.getUserInstructions().key,
        });
        toaster.create('Instruction deleted successfully', { intent: 'success' });
      },
      onError: () => {
        toaster.create('Failed to delete instruction', { intent: 'danger' });
      },
    },
    queryClient
  );

  const createInstructionMutation = useMutation(
    {
      mutationFn: (instruction: UserInstruction) =>
        userInstructionService?.createUserInstruction(instruction),
      onSuccess: () => {
        void queryClient.invalidateQueries({
          queryKey: userInstructionService?.getUserInstructions().key,
        });
        toaster.create('Instruction created successfully', { intent: 'success' });
      },
      onError: () => {
        toaster.create('Failed to create instruction', { intent: 'danger' });
      },
    },
    queryClient
  );

  const updateInstructionMutation = useMutation(
    {
      mutationFn: (instruction: UserInstruction) =>
        userInstructionService?.updateUserInstruction(instruction),
      onSuccess: () => {
        void queryClient.invalidateQueries({
          queryKey: userInstructionService?.getUserInstructions().key,
        });
        toaster.create('Instruction updated successfully', { intent: 'success' });
      },
      onError: () => {
        toaster.create('Failed to update instruction', { intent: 'danger' });
      },
    },
    queryClient
  );

  const removeInstruction = async (instructionGuid: string) => {
    const instruction = userInstructions?.find(
      userInstruction => userInstruction.instruction_guid === instructionGuid
    );
    if (instruction) {
      await deleteInstructionMutation.mutateAsync({
        instructionGuid: instruction.instruction_guid,
        isSystemInstruction: Boolean(instruction.isSystemInstruction),
      });
    }
  };

  const createNewInstruction = async (instructionInput: UserInstruction) => {
    await createInstructionMutation.mutateAsync(instructionInput);
  };

  const updateInstruction = async (instruction: UserInstruction) => {
    await updateInstructionMutation.mutateAsync(instruction);
  };

  return {
    userInstructions,
    isLoading,
    isCreating: createInstructionMutation.isPending,
    isUpdating: updateInstructionMutation.isPending,
    isRemoving: deleteInstructionMutation.isPending,
    removeInstruction,
    createNewInstruction,
    updateInstruction,
    error:
      error instanceof AxiosError && error.response?.data?.detail
        ? error.response.data.detail
        : 'Something went wrong!',
    isError,
  };
}
